// ConversationSidebar.js - Conversation list component (embedded in left sidebar)
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSession } from '../context/SessionContext';

function ConversationSidebar({ onToggle }) {
  const { user, logout } = useAuth();
  const { sessions, currentSessionId, createSession, deleteSession, selectSession, loading } = useSession();
  const [deletingId, setDeletingId] = useState(null);

  const handleNewChat = async () => {
    await createSession();
    // Close mobile sidebar after creating new chat
    if (onToggle && window.innerWidth <= 1024) {
      onToggle();
    }
  };

  const handleSelectSession = (sessionId) => {
    selectSession(sessionId);
    // Close mobile sidebar after selecting
    if (onToggle && window.innerWidth <= 1024) {
      onToggle();
    }
  };

  const handleDeleteSession = async (e, sessionId) => {
    e.stopPropagation();
    if (window.confirm('Delete this conversation? This cannot be undone.')) {
      setDeletingId(sessionId);
      await deleteSession(sessionId);
      setDeletingId(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-GB', { 
        day: 'numeric', 
        month: 'short' 
      });
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 mt-3 pt-0 gap-0">
      {/* Conversation list */}
      <div className="
        flex-1 overflow-y-auto overflow-x-hidden min-h-0 pt-0.5 pb-3 pl-0 pr-1 border-0 border-transparent border-none
        scrollbar-thin scrollbar-thumb-kaleido-purple/10 scrollbar-track-transparent hover:scrollbar-thumb-kaleido-purple/20
        [&::-webkit-scrollbar]:w-0.5 [&::-webkit-scrollbar-thumb]:rounded-full
        [mask-image:linear-gradient(to_bottom,black_0,black_calc(100%-2.5rem),transparent_100%)]
        [-webkit-mask-image:linear-gradient(to_bottom,black_0,black_calc(100%-2.5rem),transparent_100%)]
      ">
        {loading ? (
          <div className="flex flex-col items-center justify-center p-3 gap-1 text-kaleido-gray-500 text-xs">
            <div className="w-4 h-4 border-2 border-kaleido-purple/20 border-t-kaleido-purple rounded-full animate-spin"></div>
            <span>Loading...</span>
          </div>
        ) : sessions.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-3 px-2 text-center text-kaleido-gray-500 gap-0.5">
            <svg className="opacity-50" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            <span className="text-xs">No conversations yet</span>
          </div>
        ) : (
          sessions.map((session) => (
            <div
              key={session.id}
              className={`
                flex items-center justify-between px-1 py-1 mb-px rounded-md cursor-pointer transition-all duration-150 group
                ${currentSessionId === session.id ? 'bg-kaleido-purple/15' : 'hover:bg-kaleido-purple/8'}
                ${deletingId === session.id ? 'opacity-50 pointer-events-none' : ''}
              `}
              onClick={() => handleSelectSession(session.id)}
            >
              <div className="flex flex-col flex-1 min-w-0 gap-px">
                <span className="conversation-item-title text-sm font-normal text-kaleido-gray-900 whitespace-nowrap overflow-hidden text-ellipsis leading-snug">
                  {session.title || 'New conversation'}
                </span>
                <span className="conversation-item-date text-[11px] text-kaleido-gray-500 leading-tight">
                  {formatDate(session.updated_at)}
                </span>
              </div>
              <button
                className="
                  flex items-center justify-center p-0.5 shrink-0
                  bg-transparent border-none rounded-sm cursor-pointer
                  text-kaleido-gray-500 opacity-0 transition-all duration-150
                  group-hover:opacity-100 hover:bg-red-500/10 hover:text-red-500
                "
                onClick={(e) => handleDeleteSession(e, session.id)}
                aria-label="Delete conversation"
                disabled={deletingId === session.id}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                </svg>
              </button>
            </div>
          ))
        )}
      </div>

      {/* New chat button */}
      <button
        className="
          self-center inline-flex items-center justify-center gap-0.5 px-3.5 py-1.5 mb-1.5
          font-sans text-xs font-medium text-white leading-none
          bg-gradient-to-br from-kaleido-purple to-[#8050a0]
          border-none rounded-full cursor-pointer
          transition-all duration-200
          hover:-translate-y-px hover:shadow-[0_4px_12px_rgba(152,96,167,0.3)]
          active:translate-y-0
        "
        onClick={handleNewChat}
      >
        <svg className="block shrink-0" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <line x1="12" y1="5" x2="12" y2="19"/>
          <line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        <span className="leading-none">New chat</span>
      </button>

      {/* User section at bottom */}
      <div className="shrink-0 pt-2 mt-auto border-t border-kaleido-purple/15 flex flex-col gap-0.5 bg-transparent ml-1">
        <div className="flex items-center justify-start gap-3 border-0">
          <div className="flex items-center gap-1.5 min-w-0 flex-1 text-left border-0">
            <div
              className={`
                relative w-7 h-7 rounded-full shrink-0
                bg-gradient-to-br from-kaleido-purple to-kaleido-blue
                text-white flex items-center justify-center
                text-[0.625rem] font-semibold
                ${user?.is_admin
                  ? 'ring-1 ring-kaleido-purple/40 ring-offset-[1.5px] ring-offset-white shadow-[0_0_0_1px_rgba(152,96,167,0.08),0_2px_6px_-1px_rgba(152,96,167,0.35)]'
                  : ''}
              `}
              title={user?.is_admin ? 'Admin' : undefined}
              aria-label={user?.is_admin ? 'Admin user' : undefined}
            >
              {user?.display_name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || '?'}
              {user?.is_admin && (
                <span
                  aria-hidden="true"
                  className="
                    absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full
                    bg-gradient-to-br from-[#f5c76b] to-[#d98f5a]
                    ring-[1.5px] ring-white
                    shadow-[0_0_4px_rgba(217,143,90,0.55)]
                  "
                />
              )}
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-medium text-kaleido-gray-900 whitespace-nowrap overflow-hidden text-ellipsis leading-tight">
                {user?.display_name || 'User'}
              </span>
              {user?.is_admin && (
                <span className="text-[10px] uppercase tracking-[0.08em] font-semibold text-kaleido-purple/80 leading-tight">
                  Admin
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0 -mr-0.5">
            <Link
              to="/settings"
              aria-label="Settings"
              title="Settings"
              className="
                group flex items-center justify-center w-[30px] h-[30px] shrink-0
                text-kaleido-gray-400 no-underline
                rounded-md
                transition-colors duration-150
                hover:text-kaleido-purple hover:bg-kaleido-purple/8
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kaleido-purple/40
              "
            >
              <svg
                className="shrink-0 transition-transform duration-300 ease-out group-hover:rotate-45"
                width="15" height="15" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="1.75"
                strokeLinecap="round" strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
            </Link>

            <button
              onClick={logout}
              title="Sign out"
              aria-label="Sign out"
              className="
                group flex items-center justify-center w-[30px] h-[30px] shrink-0
                bg-transparent border-none cursor-pointer
                text-kaleido-gray-400
                rounded-md
                transition-colors duration-150
                hover:text-red-500 hover:bg-red-500/8
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500/40
              "
            >
              <svg
                className="shrink-0 transition-transform duration-200 ease-out group-hover:translate-x-[1px]"
                width="15" height="15" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="1.75"
                strokeLinecap="round" strokeLinejoin="round"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Powered by Kaleido - sidebar branding (mobile only) */}
        <div className="flex lg:hidden items-center justify-center gap-1.5 pt-2 mt-1 border-t border-kaleido-purple/10 opacity-50 hover:opacity-70 transition-opacity">
          <span className="text-[9px] text-kaleido-gray-500">powered by</span>
          <img 
            src="static/asset_1b.png" 
            alt="Kaleido" 
            className="h-3 w-auto" 
          />
        </div>
      </div>
    </div>
  );
}

export default ConversationSidebar;